import { PlusCircle, LogOut, Briefcase } from 'lucide-react';
import { cn } from '@/infra/utils';
import { useAppContext } from '@/store/AppContext';
import { Logo } from './Logo';

interface SidebarProps {
  currentPath: string;
  onNavigate: (path: string) => void;
}

const NAV_ITEMS = [
  { id: 'quick-estimate', path: '/app/wycena', label: 'Szybka wycena', icon: PlusCircle },
  { id: 'projects', path: '/app/projects', label: 'Moje projekty', icon: Briefcase },
];

export function Sidebar({ currentPath, onNavigate }: SidebarProps) {
  const { user, logoutUser } = useAppContext();

  return (
    <aside className="h-full bg-[#0b0d10] p-4 text-slate-300">
      <div className="flex h-full flex-col overflow-hidden rounded-[28px] border border-[#2a2f38] bg-[#15191f] shadow-2xl shadow-black/40">
        <div className="border-b border-[#2a2f38] p-5">
          <Logo />
          <p className="mt-3 text-[10px] font-black uppercase tracking-[0.28em] text-amber-300">
            Kokpit wykonawcy
          </p>
        </div>

        <nav className="flex-1 space-y-2 overflow-y-auto p-3 [scrollbar-color:#2a2f38_#15191f] [scrollbar-width:thin] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-[#2a2f38] [&::-webkit-scrollbar-track]:bg-[#15191f]">
          {NAV_ITEMS.map((item) => {
            const Icon = item.icon;
            const isActive =
              currentPath === item.path ||
              (item.path !== '/app/' && currentPath.startsWith(item.path));

            return (
              <button
                key={item.id}
                type="button"
                onClick={() => onNavigate(item.path)}
                className={cn(
                  'flex min-h-12 w-full items-center gap-3 rounded-2xl px-4 text-left text-sm font-black transition-all',
                  isActive
                    ? 'bg-amber-500 text-[#0b0d10] shadow-lg shadow-amber-500/20'
                    : 'border border-transparent text-slate-400 hover:border-[#2a2f38] hover:bg-[#0f1217] hover:text-white'
                )}
              >
                <Icon className="h-5 w-5 shrink-0" />
                <span className="truncate">{item.label}</span>
              </button>
            );
          })}
        </nav>

        <div className="border-t border-[#2a2f38] p-4">
          <div className="mb-3 rounded-2xl border border-[#2a2f38] bg-[#0f1217] p-3">
            <div className="flex items-center gap-3">
              {user?.photoURL ? (
                <img
                  src={user.photoURL}
                  alt="Avatar"
                  className="h-10 w-10 rounded-full border border-amber-400/40"
                  referrerPolicy="no-referrer"
                />
              ) : (
                <div className="flex h-10 w-10 items-center justify-center rounded-full border border-amber-400/40 bg-amber-500/10 font-black text-amber-200">
                  {user?.displayName?.charAt(0) || 'U'}
                </div>
              )}
              <div className="min-w-0">
                <p className="truncate text-sm font-black text-white">{user?.displayName || 'U\u017cytkownik'}</p>
                <p className="truncate text-xs text-slate-500">{user?.email}</p>
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={logoutUser}
            className="flex min-h-11 w-full items-center gap-3 rounded-2xl border border-[#2a2f38] bg-[#0f1217] px-4 text-sm font-bold text-slate-400 transition hover:border-amber-400/40 hover:text-white"
          >
            <LogOut className="h-4 w-4" />
            {"Wyloguj si\u0119"}
          </button>

          <div className="mt-4 flex items-center justify-between px-2 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-600">
            <span>ProWycena Core</span>
          </div>
        </div>
      </div>
    </aside>
  );
}
